/**
 * ProfilePage - user profile settings
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@stores/authStore';
import { useUIStore } from '@stores/uiStore';
import { authApi } from '@services/api';

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, setUser } = useAuthStore();
  const setUITheme = useUIStore((s) => s.setTheme);

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? '');
  const [locale, setLocale] = useState(user?.locale ?? 'en');
  const [timezone, setTimezone] = useState(user?.timezone ?? 'UTC');
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(user?.theme ?? 'system');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const profileData: Parameters<typeof authApi.updateProfile>[0] = {
        displayName,
        locale,
        timezone,
        theme,
      };
      if (avatarUrl) {
        profileData.avatarUrl = avatarUrl;
      }
      const updatedUser = await authApi.updateProfile(profileData);

      setUser(updatedUser);
      i18n.changeLanguage(locale);
      setSuccess(true);

      // Theme is applied and persisted by the UI store (useInitializeTheme
      // owns the <html> class); direct DOM manipulation here previously
      // bypassed the store, so the choice was lost on navigation.
      setUITheme(theme);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const timezones = Intl.supportedValuesOf('timeZone');

  return (
    <div className="p-6">
      <h2 className="text-lg font-medium text-neutral-900 dark:text-white mb-6">
        {t('settings.profile')}
      </h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <p className="text-sm text-green-600 dark:text-green-400">
            {t('settings.profileUpdated')}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Display Name */}
        <div>
          <label
            htmlFor="displayName"
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
          >
            {t('settings.displayName')}
          </label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
            required
          />
        </div>

        {/* Email (readonly) */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t('settings.email')}
          </label>
          <input
            type="email"
            value={user?.email ?? ''}
            disabled
            className="mt-1 block w-full rounded-md border border-neutral-300 bg-neutral-100 px-3 py-2 text-neutral-500 shadow-sm dark:border-neutral-600 dark:bg-neutral-600 dark:text-neutral-400 sm:text-sm"
          />
          <p className="mt-1 text-xs text-neutral-500">
            {t('settings.emailCannotBeChanged')}
          </p>
        </div>

        {/* Avatar URL */}
        <div>
          <label
            htmlFor="avatarUrl"
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
          >
            {t('settings.avatarUrl')}
          </label>
          <input
            type="url"
            id="avatarUrl"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            placeholder="https://example.com/avatar.jpg"
            className="mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
          />
        </div>

        {/* Theme */}
        <div>
          <label
            htmlFor="theme"
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
          >
            {t('settings.theme')}
          </label>
          <select
            id="theme"
            value={theme}
            onChange={(e) => {
              const next = e.target.value as 'light' | 'dark' | 'system';
              setTheme(next);
              setUITheme(next); // live preview + persisted client-side
            }}
            className="mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
          >
            <option value="system">{t('settings.themeSystem')}</option>
            <option value="light">{t('settings.themeLight')}</option>
            <option value="dark">{t('settings.themeDark')}</option>
          </select>
        </div>

        {/* Locale */}
        <div>
          <label
            htmlFor="locale"
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
          >
            {t('settings.language')}
          </label>
          <select
            id="locale"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
            <option value="de">Deutsch</option>
            <option value="pt">Português</option>
            <option value="ja">日本語</option>
            <option value="zh">中文</option>
          </select>
        </div>

        {/* Timezone */}
        <div>
          <label
            htmlFor="timezone"
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
          >
            {t('settings.timezone')}
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
          >
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isSaving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </form>
    </div>
  );
}
